import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { T, IMAGES } from "@/content/site";
import { buildWhatsappUrl } from "@/lib/whatsapp";

export default function PrivateLabel() {
  const { t } = useLang();
  const titleLines = t(T.privateLabel.titleLines);
  const steps = T.privateLabel.steps;
  const includes = t(T.privateLabel.includes);

  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start end", "end start"] });
  const lineHeight = useTransform(scrollYProgress, [0.1, 0.85], ["0%", "100%"]);

  const waText =
    t({
      es: "Hola SACRVM, me interesa cotizar una marca privada de mezcal (Private Label).",
      en: "Hi SACRVM, I'd like a quote for a private label mezcal.",
    });

  return (
    <section id="private-label" data-testid="private-label-section" ref={wrapRef} className="relative bg-[#0B0B0B] py-[140px] md:py-[200px] overflow-hidden">
      {/* Background subtle image */}
      <div className="absolute inset-0 opacity-[0.06]">
        <img src={IMAGES.agaveField} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-10">
          <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
          {t(T.privateLabel.overline)}
        </motion.div>

        <h2 className="font-serif text-white text-[30px] sm:text-[52px] md:text-[92px] lg:text-[112px] leading-[0.98] tracking-[-0.015em] max-w-[1200px] break-words">
          {titleLines.map((line, i) => (
            <motion.span
              key={i}
              className="mask-line"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.05 }}
            >
              <motion.span
                variants={{ hidden: { y: "110%" }, show: { y: "0%" } }}
                transition={{ duration: 1.2, delay: i * 0.12, ease: [0.19, 1, 0.22, 1] }}
                className={"inline-block " + (i === 2 ? "italic text-[color:var(--sacrvm-gold)]" : "")}
              >
                {line}
              </motion.span>
            </motion.span>
          ))}
        </h2>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-10">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="col-span-12 md:col-span-7 min-w-0 text-[color:var(--sacrvm-text-muted)] text-[17px] md:text-[19px] leading-[1.75] font-serif italic"
          >
            {t(T.privateLabel.body)}
          </motion.p>
          <div className="col-span-12 md:col-span-5 min-w-0 md:pl-10">
            <div className="overline mb-5">{t({ es: "Incluye", en: "Includes" })}</div>
            <ul className="w-full grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-white/80 text-[14px]">
              {includes.map((inc, i) => (
                <motion.li
                  key={inc}
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className="flex items-center gap-2 py-1.5 border-b border-white/5 min-w-0"
                >
                  <span className="text-[color:var(--sacrvm-gold)] text-[10px] shrink-0">◆</span>
                  <span className="break-words">{inc}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-28 md:mt-40 relative">
          <div className="overline mb-14 text-center">{t({ es: "Proceso llave en mano", en: "Turnkey process" })}</div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/8 -translate-x-1/2" />
            <motion.div
              style={{ height: lineHeight }}
              className="absolute left-1/2 top-0 w-px bg-[color:var(--sacrvm-gold)] -translate-x-1/2 origin-top"
            />
            <div className="flex flex-col gap-16 md:gap-24">
              {steps.map((s, i) => {
                const isRight = i % 2 === 1;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                    className={"relative grid grid-cols-2 items-center gap-6 md:gap-16"}
                  >
                    <div className={"absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 border border-[color:var(--sacrvm-gold)] bg-[#0B0B0B] rotate-45 z-10"} />
                    <div className={"text-right pr-6 md:pr-14 " + (isRight ? "opacity-0 pointer-events-none" : "")}>
                      {!isRight && <StepBlock n={i + 1} label={t(s)} align="right" />}
                    </div>
                    <div className={"text-left pl-6 md:pl-14 " + (!isRight ? "opacity-0 pointer-events-none" : "")}>
                      {isRight && <StepBlock n={i + 1} label={t(s)} align="left" />}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 md:mt-40 flex flex-col items-center text-center">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-serif text-white text-[32px] md:text-[52px] max-w-[860px] leading-[1.1]"
          >
            {t({ es: "Hablemos de tu marca.", en: "Let's talk about your brand." })}
          </motion.h3>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              data-testid="pl-cta-whatsapp"
              href={buildWhatsappUrl(waText)}
              target="_blank"
              rel="noreferrer"
              className="btn-sharp gold"
            >
              {t(T.privateLabel.cta)}
              <ArrowRight size={14} className="arrow" />
            </a>
            <a data-testid="pl-cta-form" href="#private-label-form" className="btn-sharp">
              {t({ es: "Formulario", en: "Form" })}
              <ArrowRight size={14} className="arrow" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepBlock({ n, label, align }) {
  return (
    <div className={"inline-block " + (align === "right" ? "text-right" : "text-left")}>
      <div className="font-serif text-[color:var(--sacrvm-gold)] text-[16px] tracking-[0.24em] mb-3">
        {String(n).padStart(2, "0")}
      </div>
      <div className="font-serif text-white text-[24px] md:text-[36px] leading-[1.1]">{label}</div>
    </div>
  );
}
